import { createSlice } from "@reduxjs/toolkit";

const BASE_API = (process.env.REACT_APP_BASE_API || "").replace(/\/+$/, "");

const getToken = () => localStorage.getItem("token");

const readGuestCart = () => {
  try {
    return JSON.parse(localStorage.getItem("cart")) || [];
  } catch {
    return [];
  }
};

const saveGuestCart = (items) =>
  localStorage.setItem("cart", JSON.stringify(items));

const toImageUrl = (src) => {
  if (!src) return "";
  if (/^(https?:)?\/\//.test(src) || src.startsWith("data:")) return src;
  const clean = String(src).replace(/^\/+/, "");
  return clean.includes("/") ? `${BASE_API}/${clean}` : `${BASE_API}/uploads/${clean}`;
};

/* Same product? Compare ids as strings so 5 and "5" count as the same,
   and treat a missing size the same as an empty size. */
const sameId = (a, b) => String(a) === String(b);
const sameItem = (a, b) => sameId(a.id, b.id) && (a.size ?? "") === (b.size ?? "");

/* server row -> the shape your components already use */
const mapServerItem = (r) => ({
  id: r.product_id, // product id, so cartItems.some(i => i.id === p.id) still works
  cartId: r.id, // cart table row id (needed for update / delete)
  name: r.name ?? r.title,
  price: Number(r.price),
  image: toImageUrl(r.image),
  qty: r.quantity,
  stock: r.stock,
});

/* small fetch helper that sends the token */
const api = async (path, options = {}) => {
  const res = await fetch(`${BASE_API}/cart${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) localStorage.removeItem("token"); // expired / invalid token
  if (!res.ok) throw new Error(data.error || data.message || "Cart request failed");
  return data;
};

/* ---------------- slice (state only) ---------------- */

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    items: getToken() ? [] : readGuestCart(), // logged-in carts are loaded by loadCart()
    isOpen: false,
    error: "",
  },
  reducers: {
    setItems: (state, action) => {
      state.items = action.payload;
      state.error = "";
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    resetCart: (state) => {
      state.items = []; // call on logout
    },

    // ----- guest-only reducers -----
    localAdd: (state, action) => {
      const { item, qty } = action.payload;
      const existing = state.items.find((i) => sameItem(i, item));
      if (existing) existing.qty += qty; // same product again -> only increase the count
      else state.items.push({ ...item, qty });
    },
    localRemove: (state, action) => {
      state.items = state.items.filter((i) => !sameItem(i, action.payload));
    },
    localIncrease: (state, action) => {
      const item = state.items.find((i) => sameItem(i, action.payload));
      if (item) item.qty += 1;
    },
    localDecrease: (state, action) => {
      const item = state.items.find((i) => sameItem(i, action.payload));
      if (item && item.qty > 1) item.qty -= 1;
    },
    localClear: (state) => {
      state.items = [];
    },

    openCart: (state) => {
      state.isOpen = true;
    },
    closeCart: (state) => {
      state.isOpen = false;
    },
  },
});

const {
  setItems,
  setError,
  localAdd,
  localRemove,
  localIncrease,
  localDecrease,
  localClear,
} = cartSlice.actions;

export const { openCart, closeCart, resetCart } = cartSlice.actions;
export default cartSlice.reducer;

/* ---------------- thunks (these are what components dispatch) ---------------- */

const findItem = (getState, id, size) =>
  getState().cart.items.find((i) => sameItem(i, { id, size })) ||
  getState().cart.items.find((i) => sameId(i.id, id)); // server items have no size

/* GET /cart -> replace Redux items with the database cart */
export const loadCart = () => async (dispatch) => {
  if (!getToken()) return;
  try {
    const rows = await api("/");
    dispatch(setItems(rows.map(mapServerItem)));
  } catch (err) {
    dispatch(setError(err.message));
  }
};

/* Run ONCE right after a successful login (token must already be saved) */
export const mergeGuestCart = () => async (dispatch) => {
  const guestItems = readGuestCart();
  try {
    if (guestItems.length) {
      await api("/merge", {
        method: "POST",
        body: JSON.stringify({
          items: guestItems.map((i) => ({ product_id: i.id, quantity: i.qty })),
        }),
      });
      localStorage.removeItem("cart");
    }
    await dispatch(loadCart());
  } catch (err) {
    dispatch(setError(err.message));
  }
};

export const addToCart = (payload) => async (dispatch, getState) => {
  const { qty = 1, ...item } = payload;

  if (!getToken()) {
    dispatch(localAdd({ item, qty }));
    saveGuestCart(getState().cart.items);
    return;
  }

  try {
    // Already in the cart? Then only increase its count (no new row).
    const existing = getState().cart.items.find((i) => sameId(i.id, item.id));

    if (existing?.cartId) {
      for (let n = 0; n < qty; n += 1) {
        await api(`/update/${existing.cartId}`, {
          method: "PUT",
          body: JSON.stringify({ action: "increment" }),
        });
      }
    } else {
      await api("/addtocart", {
        method: "POST",
        body: JSON.stringify({ product_id: item.id, quantity: qty }),
      });
    }
    await dispatch(loadCart());
  } catch (err) {
    dispatch(setError(err.message));
  }
};

export const increaseQty = (payload) => async (dispatch, getState) => {
  const { id, size } = payload;

  if (!getToken()) {
    dispatch(localIncrease({ id, size }));
    saveGuestCart(getState().cart.items);
    return;
  }
  const item = findItem(getState, id, size);
  if (!item?.cartId) return;
  try {
    await api(`/update/${item.cartId}`, {
      method: "PUT",
      body: JSON.stringify({ action: "increment" }),
    });
    await dispatch(loadCart());
  } catch (err) {
    dispatch(setError(err.message));
  }
};

export const decreaseQty = (payload) => async (dispatch, getState) => {
  const { id, size } = payload;

  if (!getToken()) {
    dispatch(localDecrease({ id, size }));
    saveGuestCart(getState().cart.items);
    return;
  }
  const item = findItem(getState, id, size);
  if (!item?.cartId) return;
  try {
    await api(`/update/${item.cartId}`, {
      method: "PUT",
      body: JSON.stringify({ action: "decrement" }),
    });
    await dispatch(loadCart());
  } catch (err) {
    dispatch(setError(err.message));
  }
};

export const removeFromCart = (payload) => async (dispatch, getState) => {
  const { id, size } = payload;

  if (!getToken()) {
    dispatch(localRemove({ id, size }));
    saveGuestCart(getState().cart.items);
    return;
  }
  const item = findItem(getState, id, size);
  if (!item?.cartId) return;
  try {
    await api(`/delete/${item.cartId}`, { method: "DELETE" });
    await dispatch(loadCart());
  } catch (err) {
    dispatch(setError(err.message));
  }
};

export const clearCart = () => async (dispatch, getState) => {
  if (!getToken()) {
    dispatch(localClear());
    saveGuestCart([]);
    return;
  }
  try {
    const ids = getState().cart.items.map((i) => i.cartId).filter(Boolean);
    await Promise.all(ids.map((cid) => api(`/delete/${cid}`, { method: "DELETE" })));
    await dispatch(loadCart());
  } catch (err) {
    dispatch(setError(err.message));
  }
};
